import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router';

import App from './app';
import LoginPage from './pages/Login';
import InicioPage from './pages/Inicio';
import EmConstrucaoPage from './pages/EmConstrucao';
import { AuthGuard, GuestGuard } from './auth/guard';
import PerfilPage from './pages/Usuario/Perfil';
import MeuImovelListPage from './pages/MeuImovel/List';
import MeuImovelDetailPage from './pages/MeuImovel/Detail';
import SubUsuariosListPage from './pages/SubUsuarios/List';
import NfseListPage from './pages/Nfse/List';
import NfseEmitirPage from './pages/Nfse/Emitir';
import NfseDetailPage from './pages/Nfse/Detail';
import ServicosTomadosListPage from './pages/ServicosTomados/List';
import ServicosTomadosDetailPage from './pages/ServicosTomados/Detail';
import PlantaoFiscalListPage from './pages/PlantaoFiscal/List';
import PlantaoFiscalDetailPage from './pages/PlantaoFiscal/Detail';
import MeioAmbienteListPage from './pages/MeioAmbiente/List';
import GuiasListPage from './pages/Guias/List';
import ParcelamentoListPage from './pages/Parcelamento/List';
import ParcelamentoNovoPage from './pages/Parcelamento/Novo';
import ParcelamentoDetailPage from './pages/Parcelamento/Detail';
import ProtocoloListPage from './pages/Protocolo/List';
import ProtocoloNovoPage from './pages/Protocolo/Novo';
import ProtocoloDetailPage from './pages/Protocolo/Detail';
import DteListPage from './pages/Dte/List';
import DteDetailPage from './pages/Dte/Detail';
import ExternoPrestadosListPage from './pages/Externo/PrestadosList';
import ExternoPrestadosDetailPage from './pages/Externo/PrestadosDetail';
import ExternoAceitarNotasPage from './pages/Externo/AceitarNotasList';
import { DashboardLayout } from './layouts/dashboard/layout';

// ----------------------------------------------------------------------
//
// Router do Portal. basename="/portal" (mesmo do vite base '/portal/').
//
// - /login              → público (GuestGuard: já logado vai pro portal)
// - rotas do dashboard   → AuthGuard + DashboardLayout (chrome Minimal)
//
// Para adicionar uma tela do contribuinte: importe a página e acrescente
// `{ path, element }` no array `children` da rota do DashboardLayout.
// ----------------------------------------------------------------------

const router = createBrowserRouter(
  [
    {
      Component: () => (
        <App>
          <Outlet />
        </App>
      ),
      children: [
        { path: '/login', element: <GuestGuard><LoginPage /></GuestGuard> },
        {
          element: (
            <AuthGuard>
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            </AuthGuard>
          ),
          children: [
            // Início do portal: ações rápidas (não cai direto na Consultar).
            { path: '/', element: <InicioPage /> },

            // NFSA-e (Avulsa) — módulo real do portal SN.
            { path: '/nfse', element: <NfseListPage /> },
            { path: '/notificacoes', element: <DteListPage /> },
            { path: '/notificacoes/:id', element: <DteDetailPage /> },
            { path: '/nfse/nova', element: <NfseEmitirPage /> },
            { path: '/nfse/:id', element: <NfseDetailPage /> },

            // Módulos KEEP (validar campos vs SN em fase posterior).
            { path: '/meu-imovel', element: <MeuImovelListPage /> },
            { path: '/meu-imovel/:id', element: <MeuImovelDetailPage /> },
            { path: '/perfil', element: <PerfilPage /> },
            { path: '/subusuarios', element: <SubUsuariosListPage /> },

            // Módulos do menu SN ainda não construídos — placeholder navegável
            // (Fases 1-4 do plano). Cada um vira tela real com dados do DB fiscal.
            { path: '/servicos-tomados', element: <ServicosTomadosListPage /> },
            { path: '/servicos-tomados/:id', element: <ServicosTomadosDetailPage /> },
            { path: '/guias', element: <GuiasListPage /> },
            { path: '/protocolo', element: <ProtocoloListPage /> },
            { path: '/protocolo/novo', element: <ProtocoloNovoPage /> },
            { path: '/protocolo/:numero', element: <ProtocoloDetailPage /> },
            { path: '/parcelamento', element: <ParcelamentoListPage /> },
            { path: '/parcelamento/novo', element: <ParcelamentoNovoPage /> },
            { path: '/parcelamento/:numero', element: <ParcelamentoDetailPage /> },
            { path: '/meio-ambiente', element: <MeioAmbienteListPage /> },
            { path: '/plantao-fiscal', element: <PlantaoFiscalListPage /> },
            { path: '/plantao-fiscal/:id', element: <PlantaoFiscalDetailPage /> },

            // Variante PJ — Contribuinte Externo (escrituração de ISS).
            // Consultar Prestados/Tomados = leitura real. Consultar Tomados reusa
            // as telas de Serviços Tomados (mesma query nf.cpfcnpj=login). Escriturar
            // Prestados reusa o wizard de emissão NFSA-e (rps.gerar_nfse). Escriturar
            // Tomados fica p/ UAT (proc de escrituração-tomado não mapeada).
            { path: '/externo/prestados', element: <ExternoPrestadosListPage /> },
            { path: '/externo/prestados/novo', element: <NfseEmitirPage /> },
            { path: '/externo/prestados/:id', element: <ExternoPrestadosDetailPage /> },
            { path: '/externo/tomados', element: <ServicosTomadosListPage /> },
            { path: '/externo/tomados/:id', element: <ServicosTomadosDetailPage /> },
            { path: '/externo/tomados/novo', element: <EmConstrucaoPage titulo="Escriturar Serviços Tomados" /> },
            { path: '/externo/aceitar-notas', element: <ExternoAceitarNotasPage /> },
          ],
        },
      ],
    },
  ],
  { basename: '/portal' }
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
